import { getOwnerToken, OWNER_TOKEN_HEADER } from './ownerToken'
import { sanitizeZones, type AlertZone } from './alertZones'

/**
 * Client side of browser push notifications. Registers the service worker,
 * asks for permission, and subscribes the browser with the server so it can be
 * pushed a notification whenever someone else drops a marker — even when the
 * app is closed.
 *
 * The subscription is tagged with the current UI language so the server can
 * localize each notification to its recipient; call `refreshPushLanguage` when
 * the user switches languages. It also carries the alert zones drawn on the
 * map, which the server uses to skip pins outside them.
 */

const SW_URL = '/sw.js'

// Zones are a per-browser preference and this is their source of truth; the
// copy stored with the subscription only exists so the server can filter.
const ZONES_STORAGE_KEY = 'info-traflic:alert-zones'

export interface PushStatus {
  supported: boolean
  permission: NotificationPermission
  subscribed: boolean
}

export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

// VAPID keys arrive as base64url; the Push API wants raw bytes.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) {
    output[i] = raw.charCodeAt(i)
  }
  return output
}

/** The zones drawn on this device, or an empty list meaning "notify anywhere". */
export function loadAlertZones(): AlertZone[] {
  try {
    const raw = localStorage.getItem(ZONES_STORAGE_KEY)
    return raw ? sanitizeZones(JSON.parse(raw)) : []
  } catch {
    // Unreadable or unparseable storage falls back to notify-everywhere, which
    // is the safe direction to fail in: too many alerts, never too few.
    return []
  }
}

async function fetchVapidKey(): Promise<string> {
  try {
    const res = await fetch('/api/push/vapid-public-key')
    const data = await res.json()
    return typeof data.key === 'string' ? data.key : ''
  } catch {
    return ''
  }
}

function postSubscribe(
  subscription: PushSubscription,
  lang: string,
  zones: AlertZone[]
): Promise<Response> {
  return fetch('/api/push/subscribe', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Links the subscription to this browser, so the server skips notifying
      // us about our own pins.
      [OWNER_TOKEN_HEADER]: getOwnerToken(),
    },
    body: JSON.stringify({
      subscription: subscription.toJSON(),
      lang: (lang || 'en').split('-')[0],
      zones,
    }),
  })
}

function postUnsubscribe(endpoint: string): Promise<Response> {
  return fetch('/api/push/unsubscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint }),
  })
}

export async function getPushStatus(): Promise<PushStatus> {
  if (!isPushSupported()) {
    return { supported: false, permission: 'denied', subscribed: false }
  }
  let subscribed = false
  try {
    const registration = await navigator.serviceWorker.getRegistration()
    if (registration) {
      subscribed = Boolean(await registration.pushManager.getSubscription())
    }
  } catch {
    // Treat any lookup failure as "not subscribed"; enabling will retry.
  }
  return { supported: true, permission: Notification.permission, subscribed }
}

/**
 * Ask for permission and subscribe this browser. Must be called from a user
 * gesture (the permission prompt requires one). Returns the resulting state so
 * the caller can reflect a denial in the UI.
 */
export async function enablePush(lang: string): Promise<PushStatus> {
  if (!isPushSupported()) {
    return { supported: false, permission: 'denied', subscribed: false }
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    return { supported: true, permission, subscribed: false }
  }

  const key = await fetchVapidKey()
  if (!key) {
    // Server has no VAPID keys configured; nothing to subscribe to.
    return { supported: true, permission, subscribed: false }
  }

  const registration = await navigator.serviceWorker.register(SW_URL)
  await navigator.serviceWorker.ready

  let subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      // Cast: the DOM lib types the key as BufferSource, but a Uint8Array over a
      // fresh ArrayBuffer satisfies it at runtime.
      applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
    })
  }

  await postSubscribe(subscription, lang, loadAlertZones())
  return { supported: true, permission, subscribed: true }
}

export async function disablePush(): Promise<void> {
  if (!isPushSupported()) return
  const registration = await navigator.serviceWorker.getRegistration()
  if (!registration) return
  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return
  // Drop it server-side first, then locally — a leftover server record would
  // only get pruned on its next failed push.
  await postUnsubscribe(subscription.endpoint)
  await subscription.unsubscribe()
}

/** Re-register the current language so notifications keep matching the UI. */
export async function refreshPushLanguage(lang: string): Promise<void> {
  if (!isPushSupported() || Notification.permission !== 'granted') return
  const registration = await navigator.serviceWorker.getRegistration()
  if (!registration) return
  const subscription = await registration.pushManager.getSubscription()
  if (subscription) await postSubscribe(subscription, lang, loadAlertZones())
}

/**
 * Persist the zones locally and, if this browser is subscribed, hand them to
 * the server so it can filter. When it isn't, they ride along with the next
 * subscribe.
 */
export async function saveAlertZones(
  zones: AlertZone[],
  lang: string
): Promise<void> {
  try {
    localStorage.setItem(ZONES_STORAGE_KEY, JSON.stringify(zones))
  } catch {
    // Private mode: the zones live for this page session only.
  }

  if (!isPushSupported() || Notification.permission !== 'granted') return
  const registration = await navigator.serviceWorker.getRegistration()
  if (!registration) return
  const subscription = await registration.pushManager.getSubscription()
  if (subscription) await postSubscribe(subscription, lang, zones)
}
